import { LocalizedString } from './localized-string.model';

const DEFAULT_FALLBACK_LOCALE: keyof LocalizedString = 'en-US';
const SUPPORTED_LOCALES = Object.keys(new LocalizedString());

const findKey = (supportedLocales: string[], predicate: (supportedLocale: string) => boolean) =>
  supportedLocales.find(predicate) as keyof LocalizedString | undefined;

/**
 * Resolves any locale string (Salesforce `pt_PT`, browser `es-MX`, ...) to a `LocalizedString` key.
 * Order: exact match, then the base language key (`es-MX` -> `es`), then the first regional key of
 * that base language (`zh` -> `zh-CN`), then the fallback.
 */
export const resolveSupportedLocale = (
  locale: string | null | undefined,
  fallback: keyof LocalizedString = DEFAULT_FALLBACK_LOCALE,
): keyof LocalizedString => {
  if (!locale) {
    return fallback;
  }

  const normalisedLocale = locale.replace('_', '-').toLowerCase();
  const baseLanguage = normalisedLocale.split('-')[0];

  return (
    findKey(SUPPORTED_LOCALES, (supportedLocale) => supportedLocale.toLowerCase() === normalisedLocale) ??
    findKey(SUPPORTED_LOCALES, (supportedLocale) => supportedLocale.toLowerCase() === baseLanguage) ??
    findKey(SUPPORTED_LOCALES, (supportedLocale) => supportedLocale.toLowerCase().startsWith(`${baseLanguage}-`)) ??
    fallback
  );
};

/**
 * Reads the text for a resolved locale key, treating empty strings as missing. Order: the exact key,
 * then its base language (`pt-PT` -> `pt`), then the fallback key.
 */
export const getLocalizedValue = (
  localizedString: Partial<LocalizedString> | null | undefined,
  language: keyof LocalizedString,
  fallback: keyof LocalizedString = DEFAULT_FALLBACK_LOCALE,
): string | undefined => {
  if (!localizedString) {
    return undefined;
  }

  const baseLanguage = language.split('-')[0] as keyof LocalizedString;

  return localizedString[language] || localizedString[baseLanguage] || localizedString[fallback] || undefined;
};
